import { Box, Flex, FlexProps } from "@chakra-ui/react";
import React, { useEffect } from "react";

import { useQuizActions, useQuizSnapshot } from "../internal/state";
import { ProgressIndicator } from "./progress";
import { useQuizConfig } from "./quizProvider";
import { getSlideProperties } from "../internal/tracking";
import { ContainerPropsOverride } from "./types";
import { useDevTool } from "@base/lib/dev-toolbar/devTools";
import { QuizSlidesDebugPanel } from "./slidesDebugPanel";

export type QuizUIProps = {
  children?: React.ReactNode;
  headerComponent?: React.ReactNode;
  containerProps?: FlexProps;
  progressContainerProps?: {
    showTitle?: boolean;
    progressBarOverrides?: ContainerPropsOverride["progressBar"];
  } & FlexProps;
};

export function QuizUI({
  children,
  headerComponent,
  containerProps,
  progressContainerProps,
}: QuizUIProps) {
  useStateSyncToUrl();

  const config = useQuizConfig();
  const snap = useQuizSnapshot();
  // Toggled from the dev toolbar ("quiz slides").
  const showSlidePicker = useDevTool("quizSlides");

  useEffect(() => {
    if (snap.currentSlide) {
      const slide = snap.currentSlide;
      config.onSlideChange?.({ id: snap.currentSlide.id });
      config.onTrackingEvent?.({
        name: "slide-entered",
        properties: {
          // @ts-expect-error
          ...getSlideProperties(slide),
        },
      });
    }
  }, [snap.currentSlide]);

  useEffect(() => {
    const root = document.body;
    root!.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [snap.currentSlideID]);

  return (
    <Flex id="#quiz-lib-root" direction={"column"} {...containerProps} height={"100%"}>
      <QuizBg />
      {showSlidePicker && <QuizSlidesDebugPanel />}

      {headerComponent && <Box id="header-wrapper">{headerComponent}</Box>}

      <ProgressIndicator {...progressContainerProps} />

      <Flex position={"relative"} flexGrow={1} width={"100%"} justifyContent={"center"}>
        {children}
      </Flex>
    </Flex>
  );
}

const PARAM_SLIDE_ID = "slideid";

function useStateSyncToUrl() {
  const snap = useQuizSnapshot();
  const actions = useQuizActions();

  // update URL parameter when state changes
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paramValue = params.get(PARAM_SLIDE_ID);
    if (paramValue === snap.currentSlideID) {
      return;
    }
    if (!snap.currentSlideID) {
      return;
    }

    params.set(PARAM_SLIDE_ID, snap.currentSlideID);
    history.pushState(null, "", `?${params.toString()}`);
  }, [snap.currentSlideID]);

  // Effect to update the state when the URL parameter changes
  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const paramValue = params.get(PARAM_SLIDE_ID);
      if (paramValue) {
        actions.goToSlideID(paramValue);
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);
}

/**
 * Renders a full screen background below the quiz
 * It's a separate component for performance reasons
 */
function QuizBg() {
  const snap = useQuizSnapshot();
  const customBg = snap.currentSlide?.quizContainerProps?.bg;
  const customBgGradient = snap.currentSlide?.quizContainerProps?.bgGradient;

  return (
    <Box
      id="bg-div"
      zIndex={-1}
      width={"100%"}
      height={"100vh"}
      position={"fixed"}
      background={customBg}
      bgGradient={customBgGradient ? customBgGradient : customBg ? customBg : undefined}
    />
  );
}
